import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import moment from "moment";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

const ClientsReport = ({ data, location }) => {
  const { orders = [], count, currency } = data || {};
  const { t } = useTranslation();

  const sortedOrders = useMemo(() => {
    const clone = Array.isArray(orders) ? [...orders] : [];
    return clone.sort((a, b) => {
      const aId = a?.additionalInfo?.authenticatedUser?.id ?? "";
      const bId = b?.additionalInfo?.authenticatedUser?.id ?? "";
      if (aId < bId) return -1;
      if (aId > bId) return 1;
      return 0;
    });
  }, [orders]);

  const groupedByClient = useMemo(() => {
    const groups = [];
    const index = new Map();
    sortedOrders.forEach((order) => {
      const auth = order?.additionalInfo?.authenticatedUser;
      const clientId = auth?.id ?? "unknown";
      const emp = auth?.employee;
      const empName =
        emp && (emp.firstName || emp.lastName)
          ? ` [ ${emp.firstName || ""} ${emp.lastName || ""}`.trim() + ` ]`
          : "";
      const clientName = auth?.name || "";
      if (!index.has(clientId)) {
        index.set(clientId, groups.length);
        groups.push({ clientId, clientName, empName, orders: [] });
      }
      groups[index.get(clientId)].orders.push(order);
    });
    return groups;
  }, [sortedOrders]);

  const exportToCSV = () => {
    try {
      const csvRows = [];
      const headers = [
        t("pages.dsokOrders.customer"),
        t("pages.dsokReports.orderId"),
        t("pages.dsokReports.paymentMethod"),
        t("pages.dsokReports.date"),
        t("pages.dsokReports.time"),
        t("pages.dsokReports.grandTotalWithoutVat"),
        t("pages.dsokReports.vatTotal"),
        t("pages.dsokReports.grandTotal"),
      ];
      csvRows.push(headers.join(","));

      const groups = Array.isArray(groupedByClient) ? groupedByClient : [];
      groups.forEach((group) => {
        const orders = Array.isArray(group.orders) ? group.orders : [];
        orders.forEach((order) => {
          const createdDate = order?.createdAt
            ? moment(order.createdAt).format("DD.MM.YYYY")
            : "";
          const createdTime = order?.createdAt
            ? moment(order.createdAt).format("HH:mm:ss")
            : "";
          const safeClient = `${group.clientName || ""}`.replace(/"/g, '""');
          const safeOrderId = `${order?.externalOrderId || ""}`.replace(
            /"/g,
            '""'
          );
          const paymentMethod =
            t("pages.dsokOrders.paymentMethods." + order?.payment?.method) ||
            t("pages.dsokOrders.paymentMethods." + order?.payment?.method) ||
            "";
          const safePaymentMethod = `${paymentMethod}`.replace(/"/g, '""');
          const gtWov = order?.grandTotalWithoutVat;
          const vat = order?.vatTotal;
          const gt = order?.grandTotal;
          const row = [
            `"${safeClient}"`,
            `"${safeOrderId}"`,
            `"${safePaymentMethod}"`,
            `"${createdDate}"`,
            `"${createdTime}"`,
            gtWov !== undefined && gtWov !== null && gtWov !== ""
              ? Number(gtWov).toFixed(2)
              : "",
            vat !== undefined && vat !== null && vat !== ""
              ? Number(vat).toFixed(2)
              : "",
            gt !== undefined && gt !== null && gt !== ""
              ? Number(gt).toFixed(2)
              : "",
          ];
          csvRows.push(row.join(","));
        });
      });

      const csvContent =
        "data:text/csv;charset=utf-8,\uFEFF" + csvRows.join("\n");
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", "clients_report.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      // Surface the error for easier debugging
      // eslint-disable-next-line no-console
      console.error("ClientsReport CSV export failed", error);
      if (typeof window !== "undefined") {
        window.alert("CSV export failed. See console for details.");
      }
    }
  };

  return (
    <div>
      {/* Section for overall total */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 2,
          mb: 2,
        }}
      >
        <Tooltip title={t("pages.dsokReports.exportToCSV")}>
          <IconButton color="primary" onClick={exportToCSV} sx={{ ml: 2 }}>
            <FileDownloadIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("pages.dsokReports.orderId")}</TableCell>
              <TableCell>{t("pages.dsokReports.paymentMethod")}</TableCell>
              <TableCell>{t("pages.presentation.createdAt")}</TableCell>
              <TableCell align="right">
                {t("pages.dsokReports.grandTotalWithoutVat")}
              </TableCell>
              <TableCell align="right">
                {t("pages.dsokReports.vatTotal")}
              </TableCell>
              <TableCell align="right">
                {t("pages.dsokReports.grandTotal")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupedByClient.map((group) => (
              <React.Fragment key={group.clientId}>
                <TableRow>
                  <TableCell
                    colSpan={6}
                    sx={{
                      fontWeight: 700,
                      background: "#E44A77",
                      color: "#fff",
                    }}
                  >
                    <span style={{ color: "#fff" }}>{group.clientName}</span>
                  </TableCell>
                </TableRow>
                {group.orders.map((order) => {
                  const created = order?.createdAt
                    ? moment(order.createdAt).format("DD.MM.YYYY HH:mm:ss")
                    : "";
                  const paymentMethod =
                    t(
                      "pages.dsokOrders.paymentMethods." +
                        order?.payment?.method
                    ) ||
                    t(
                      "pages.dsokOrders.paymentMethods." +
                        order?.payment?.method
                    ) ||
                    "";
                  const gtWOV = order?.grandTotalWithoutVat;
                  const vat = order?.vatTotal;
                  const gt = order?.grandTotal;
                  return (
                    <TableRow
                      key={order?._id || order?.externalOrderId || created}
                    >
                      <TableCell>{order?.externalOrderId}</TableCell>
                      <TableCell>{paymentMethod}</TableCell>
                      <TableCell>
                        {created}{" "}
                        <span style={{ fontSize: "12px", color: "#E44A77" }}>
                          {group?.empName}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        {gtWOV !== undefined && gtWOV !== null
                          ? `${Number(gtWOV).toFixed(2)} ${currency || ""}`
                          : ""}
                      </TableCell>
                      <TableCell align="right">
                        {vat !== undefined && vat !== null
                          ? `${Number(vat).toFixed(2)} ${currency || ""}`
                          : ""}
                      </TableCell>
                      <TableCell align="right">
                        {gt !== undefined && gt !== null
                          ? `${Number(gt).toFixed(2)} ${currency || ""}`
                          : ""}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {/* Client totals */}
                {(() => {
                  const totals = (group.orders || []).reduce(
                    (acc, o) => {
                      const wov = Number(o?.grandTotalWithoutVat) || 0;
                      const vat = Number(o?.vatTotal) || 0;
                      const gt = Number(o?.grandTotal) || 0;
                      acc.wov += wov;
                      acc.vat += vat;
                      acc.gt += gt;
                      return acc;
                    },
                    { wov: 0, vat: 0, gt: 0 }
                  );
                  return (
                    <TableRow>
                      <TableCell
                        colSpan={3}
                        align="right"
                        sx={{ fontWeight: 700, background: "#fafafa" }}
                      >
                        {t("pages.dsokReports.total")}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 700, background: "#fafafa" }}
                      >
                        {`${totals.wov.toFixed(2)} ${currency || ""}`}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 700, background: "#fafafa" }}
                      >
                        {`${totals.vat.toFixed(2)} ${currency || ""}`}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 700, background: "#fafafa" }}
                      >
                        {`${totals.gt.toFixed(2)} ${currency || ""}`}
                      </TableCell>
                    </TableRow>
                  );
                })()}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default ClientsReport;
